import { KeyboardArrowRight } from '@mui/icons-material';
import { alpha, MenuItem as MuiMenuItem, styled } from '@mui/material';

interface StyledMenuItemProps {
  active: string;
  open: boolean;
  nested: string;
}

export const StyledMenuItem = styled(MuiMenuItem, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'open' && prop !== 'nested',
})<StyledMenuItemProps>(({ theme, active, nested, open }) => {
  const isActive = active === 'true';
  const isNested = nested === 'true';
  return {
    padding: '5px 12px',
    margin: isNested ? '5px 0' : '5px 10px',
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: open ? 'flex-start' : 'center',
    minHeight: '48px',
    transition: 'background-color 120ms ease, color 120ms ease',
    backgroundColor: isActive ? alpha(theme.palette.text.primary, 0.06) : 'transparent',
    color: theme.palette.text.primary,
    border: 'none',
    '&:hover': {
      backgroundColor: isActive ? alpha(theme.palette.text.primary, 0.08) : alpha(theme.palette.text.primary, 0.04),
    },
    '&.Mui-focusVisible': {
      backgroundColor: isActive ? alpha(theme.palette.text.primary, 0.08) : alpha(theme.palette.text.primary, 0.04),
    },
    '&:focus': {
      backgroundColor: isActive ? alpha(theme.palette.text.primary, 0.06) : 'transparent',
    },
    '& .MuiSvgIcon-root': {
      color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
      fontSize: '22px',
      transition: 'color 120ms ease',
    },
    '&:hover .MuiSvgIcon-root': {
      color: theme.palette.primary.main,
    },
    '& > div': {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      flex: open ? 1 : 'none',
      minWidth: 0,
      justifyContent: open ? 'flex-start' : 'center',
    },
  };
});

export const ItemText = styled('span')<{ active?: boolean }>(({ active }) => ({
  flex: 1,
  minWidth: 0,
  whiteSpace: 'normal',
  overflowWrap: 'break-word',
  fontWeight: active ? 500 : 400,
  fontSize: '13px',
  lineHeight: '20px',
  color: 'inherit',
  letterSpacing: '-0.01em',
}));

export const TextBadgeGroup = styled('div')(() => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  minWidth: 0,
  overflow: 'hidden',
}));

export const IconWrapper = styled('div')(() => ({
  width: '32px',
  height: '32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}));

interface ArrowProps {
  active?: string;
}

export const Arrow = styled(KeyboardArrowRight, {
  shouldForwardProp: (prop) => prop !== 'active',
})<ArrowProps>(({ active, theme }) => ({
  transform: `rotate(${active === 'true' ? '90deg' : 0})`,
  transition: 'transform 200ms ease',
  color: theme.palette.text.secondary,
  fontSize: '20px',
  flexShrink: 0,
  marginLeft: '4px',
}));

// Notification badge — red card shape
export const MenuBadge = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  color: theme.palette.common.white,
  borderRadius: '4px',
  padding: '0 4px',
  fontSize: '10px',
  fontWeight: 700,
  lineHeight: 1,
  minWidth: '16px',
  height: '16px',
  boxSizing: 'border-box',
  display: 'inline-grid',
  placeItems: 'center',
  flexShrink: 0,
  textAlign: 'center',
  boxShadow: `0 1px 2px ${alpha(theme.palette.error.main, 0.25)}`,
}));
